import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

function Lessons() {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [taskCountToDelete, setTaskCountToDelete] = useState(null);
  const [taskCountLoading, setTaskCountLoading] = useState(false);

  // Fetch lessons from Firestore
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const lessonsRef = collection(db, "node_lessons");
        const q = query(lessonsRef, orderBy("title"));
        const querySnapshot = await getDocs(q);

        const lessonsData = [];
        querySnapshot.forEach((doc) => {
          lessonsData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        // Super admins see all lessons (including preview)
        setLessons(lessonsData);
        setLoading(false);
      } catch (error) {
        setError("Failed to load lessons");
        setLoading(false);
      }
    };

    fetchLessons();
  }, []);

  const handleEditLesson = (lessonId) => {
    navigate(`/admin/lesson/${lessonId}`);
  };

  const handleDeleteLesson = async (lessonId) => {
    const lesson = lessons.find((l) => l.id === lessonId);
    setLessonToDelete(lesson);
    setShowDeleteModal(true);

    // Fetch task count for this lesson
    setTaskCountLoading(true);
    setTaskCountToDelete(null);
    try {
      const lessonRef = doc(db, "node_lessons", lessonId);
      const tasksRef = collection(db, "node_lesson_tasks");
      const tasksQuery = query(tasksRef, where("lesson_id", "==", lessonRef));
      const countSnap = await getCountFromServer(tasksQuery);
      const count = countSnap.data().count || 0;
      setTaskCountToDelete(count);
    } catch (countError) {
      console.error("Error counting lesson tasks:", countError);
      setTaskCountToDelete(0);
    } finally {
      setTaskCountLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!lessonToDelete) return;

    try {
      // Delete related tasks first
      const lessonRef = doc(db, "node_lessons", lessonToDelete.id);
      const tasksRef = collection(db, "node_lesson_tasks");
      const tasksQuery = query(tasksRef, where("lesson_id", "==", lessonRef));
      const tasksSnapshot = await getDocs(tasksQuery);

      if (!tasksSnapshot.empty) {
        await Promise.all(tasksSnapshot.docs.map((d) => deleteDoc(d.ref)));
      }

      // Delete the lesson document
      await deleteDoc(lessonRef);

      // Remove from local state
      setLessons(lessons.filter((lesson) => lesson.id !== lessonToDelete.id));

      const deletedTasksCount = taskCountToDelete ?? tasksSnapshot.size ?? 0;
      toast.success(
        `Lesson deleted successfully${
          deletedTasksCount ? ` along with ${deletedTasksCount} task(s)` : ""
        }!`
      );
      setShowDeleteModal(false);
      setLessonToDelete(null);
      setTaskCountToDelete(null);
      setTaskCountLoading(false);
    } catch (error) {
      console.error("Error deleting lesson:", error);
      toast.error("Error deleting lesson. Please try again.");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLessonToDelete(null);
    setTaskCountToDelete(null);
    setTaskCountLoading(false);
  };

  const handleNewLesson = () => {
    navigate("/admin/lesson/new");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading lessons...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Row */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Lessons</h1>
            <button
              onClick={handleNewLesson}
              className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-400 transition-colors flex items-center space-x-2"
            >
              <PlusIcon className="w-5 h-5" />
              <span>New Lesson</span>
            </button>
          </div>

          {/* Table */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Units
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keywords
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lessons.map((lesson) => (
                  <tr key={lesson.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {lesson.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lesson.units && Array.isArray(lesson.units)
                        ? lesson.units.join(", ")
                        : "No units"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(lesson.keywords && Array.isArray(lesson.keywords)
                        ? lesson.keywords.length
                        : 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          lesson.isPreview === true
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {lesson.isPreview === true ? "Preview" : "Published"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2.5">
                        <button
                          onClick={() => handleEditLesson(lesson.id)}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteLesson(lesson.id)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {lessons.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No lessons found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Lesson
                </h3>
                <button
                  onClick={cancelDelete}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="mt-2">
                <p className="text-sm text-gray-500 mb-2">
                  Are you sure you want to delete "{lessonToDelete?.title}"?
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {taskCountLoading
                    ? "Counting related tasks..."
                    : `This will also delete ${
                        taskCountToDelete ?? 0
                      } related task(s). This action cannot be undone.`}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDelete}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Lessons;
